import * as React from "react";
import { useMemo } from "react";
import { styled } from "@mui/material";
import { DiseaseMatrices } from "fe-common/utils/zoomUtil";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { InChatDemoReasoningIcon } from "./inChatDemoReasoningIcon";
import { QualifiedFeature } from "fe-common/graphql";
import { ShowGraphButton } from "./showGraphButton";
import { DemoZoomGraphButtonSupport } from "./useDemoReasoningController";
import { toFeature } from "./demoModeGraphView";

//region [[ Styles ]]

const DiseaseReasoningListItemView = styled("div")({
  display: "flex",
  flexFlow: "column",
  width: "100%",
});

const DiseaseTitle = styled("div")({
  display: "flex",
  padding: "5px 5px",
  borderBottom: `1px solid ${PatientColorMap.DefaultDark}`,
  justifyContent: "space-between",
  alignItems: "center",
});

const ReasoningContent = styled("div")({
  display: "flex",
  flexDirection: "row",
  padding: "4px 20px",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
});

const ReasoningText = styled("div")({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  alignItems: "center",
  flexBasis: "50%",
  fontSize: "12px",
  padding: "5px",
  "&.positive": {
    color: "rgba(76, 217, 100, 1)",
  },
  "&.negative": {
    color: "red",
  },
});

const DiseaseName = styled("span")({
  fontWeight: 500,
});

const Buttons = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DiseaseReasoningListItemProps
  extends DemoZoomGraphButtonSupport,
    DemoZoomGraphButtonSupport {
  isPositive: boolean;
  disease: DiseaseMatrices;
  onDiseaseReasoningClick: (d: DiseaseMatrices) => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const DiseaseReasoningListItem = ({
  isPositive,
  disease,
  ...props
}: DiseaseReasoningListItemProps) => {
  const post = React.useMemo(
    () => (isPositive ? disease.postIfTrue : disease.postIfFalse),
    [isPositive, disease],
  );

  const delta = (post - disease.prob) * 100;

  const probstr = (disease.prob * 100).toPrecision(2).padStart(5, " ") + "%";

  const deltastr = delta
    ? (delta <= 0 ? "" : "+") + delta.toPrecision(2).padStart(8, " ")
    : null;

  const sensstr = disease.sensitivity?.value
    ? ((disease.sensitivity?.value || 0) * 100)
        .toPrecision(3)
        .padStart(5, " ") + "%"
    : null;

  const specstr = disease.specificity
    ? ((disease.specificity || 0) * 100).toPrecision(3).padStart(5, " ") + "%"
    : null;

  const diseaseAsQf: QualifiedFeature = useMemo(
    () => ({
      id: disease.diseaseId,
      name: disease.diseaseName,
      negate: false,
      feature: toFeature(disease),
      qualifiers: [],
      skip: false,
      mandatory: null,
    }),
    [disease],
  );

  return (
    <DiseaseReasoningListItemView>
      <DiseaseTitle>
        <DiseaseName>{disease.diseaseName}</DiseaseName>
        <Buttons>
          <ShowGraphButton onClick={props.onShowGraph} qf={diseaseAsQf} />
          <InChatDemoReasoningIcon
            onClick={() => props.onDiseaseReasoningClick(disease)}
          />
        </Buttons>
      </DiseaseTitle>
      <ReasoningContent>
        <ReasoningText>Prior Prb</ReasoningText>
        <ReasoningText>{probstr}</ReasoningText>
        <ReasoningText>Post Prb +/-</ReasoningText>
        {deltastr && (
          <>
            <ReasoningText className={delta >= 0 ? "positive" : "negative"}>
              {deltastr}
            </ReasoningText>
          </>
        )}
        {sensstr && (
          <>
            <ReasoningText>Sensitivity</ReasoningText>
            <ReasoningText>{sensstr}</ReasoningText>
          </>
        )}
        {specstr && (
          <>
            <ReasoningText>Specificity</ReasoningText>
            <ReasoningText>{specstr}</ReasoningText>
          </>
        )}
      </ReasoningContent>
    </DiseaseReasoningListItemView>
  );
};
